import { useEffect, useRef, useState, EffectCallback } from 'react'
import isEqual from 'lodash/isEqual'
import { SCREEN_BREAKPOINTS } from '../utils/helpers/ui.helpers'

type WindowSize = {
  width: number | undefined
  height: number | undefined
}

type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl'

type StateRefReturn<T> = [T, (newValue: T) => void, React.MutableRefObject<T>]

/*
 * Exploits the useEffect hook to define a custom hook that
 * executes a function only once, after the initial render!
 */
export const useMountEffect = (funct: EffectCallback): void => useEffect(funct, [])

/*
 * Keeps the previous value of a state. Usage example:
 * const [count, setCount] = useState(0);
 * const prevCount = usePrevious(count);
 */
export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T | undefined>(undefined)
  useEffect(() => {
    ref.current = value
  }, [value])
  return ref.current
}

export const useQueryParams = (): URLSearchParams => new URLSearchParams(window.location.search)

/**
 * useState and useRef combined together, to update state and re-render component,
 * but use always the last updated value
 * @link https://github.com/Aminadav/react-useStateRef#readme
 */
export const useStateRef = <T>(defaultValue: T): StateRefReturn<T> => {
  const [state, setState] = useState<T>(defaultValue)
  const ref = useRef<T>(defaultValue)
  ref.current = state
  return [
    state,
    (newValue: T) => {
      ref.current = newValue
      return setState(newValue)
    },
    ref,
  ]
}

export const useOnInit = (): boolean => {
  const isMountRef = useRef<boolean>(true)

  useEffect(() => {
    isMountRef.current = false
  }, [])

  return isMountRef.current
}

export const useWindowSize = (): WindowSize => {
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: undefined,
    height: undefined,
  })

  useEffect(() => {
    // Handler to call on window resize
    function handleResize(): void {
      // Set window width/height to state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    // Add event listener
    window.addEventListener('resize', handleResize)

    // Call handler right away so state gets updated with initial window size
    handleResize()

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowSize
}

export const usePageTitle = (title?: string) => {
  useEffect(() => {
    document.title = title ?? 'Agami'
  }, [title])
}

export const useDeepCompareEffect = (
  val: Record<string, unknown> | unknown[] | string | number | boolean | null | undefined
): [boolean, React.Dispatch<React.SetStateAction<boolean>>] => {
  const [isChanged, setIsChanged] = useState<boolean>(false)
  const prev = usePrevious(val)
  useEffect(() => {
    if (prev) {
      const changed = !isEqual(val, prev)
      setIsChanged(changed)
    } else {
      setIsChanged(false)
    }
    return () => setIsChanged(false)
  }, [val])
  return [isChanged, setIsChanged]
}

export const useDeepCompareObjectsEffect = (
  obj1: Record<string, unknown> | unknown[] | string | number | boolean | null | undefined | object,
  obj2: Record<string, unknown> | unknown[] | string | number | boolean | null | undefined | object
): [boolean, React.Dispatch<React.SetStateAction<boolean>>] => {
  const [isChanged, setIsChanged] = useState<boolean>(false)
  useEffect(() => {
    if (obj1 && obj2) {
      const changed = !isEqual(obj1, obj2)
      setIsChanged(changed)
    } else {
      setIsChanged(false)
    }
    return () => setIsChanged(false)
  }, [obj1, obj2])
  return [isChanged, setIsChanged]
}

export const useScreenBreakpoint = (): Breakpoint => {
  const { width } = useWindowSize()

  if (width && width < SCREEN_BREAKPOINTS.md) {
    return 'sm'
  }

  if (width && width < SCREEN_BREAKPOINTS.lg) {
    return 'md'
  }

  if (width && width < SCREEN_BREAKPOINTS.xl) {
    return 'lg'
  }

  if (width && width < SCREEN_BREAKPOINTS['2xl']) {
    return 'xl'
  }

  return '2xl'
}
